import { ArchivedView, ListContentFilter, ListOwnership } from "@/enum/list.enum";

/**
 * Inputs deciding whether the List overview shows the "Create your first list" onboarding empty
 * state (PREC-3623). Kept as a pure predicate so the decision can be unit-tested without rendering
 * the whole {@link ListTable}.
 */
export interface MyListsEmptyStateParams {
	ownership: ListOwnership;
	/** The Search Results tab is active. */
	isSearchTab: boolean;
	/** A (debounced) free-text search term is present. */
	hasSearchQuery: boolean;
	contentFilter: ListContentFilter;
	archivedView: ArchivedView;
	/** A non-default source-type filter is applied. */
	hasSourceFilter: boolean;
	isLoading: boolean;
	isFetching: boolean;
	/**
	 * Total list count from the page meta. Declared `number`, but the API surfaces it as a STRING at
	 * runtime (it's a raw SQL `COUNT(*)`, uncoerced by PageMetaDTO — see web CLAUDE.md §6), so it must
	 * be coerced before comparison.
	 */
	itemCount: number;
}

/**
 * The onboarding empty state is shown only for the *unfiltered* My Lists scope with zero results —
 * the case the ticket calls out ("only My Lists can be empty for new users"). Any active
 * search/content/source filter or the archived view is a normal filtered-to-zero result and falls
 * back to the table's default "no data" message; Team / All never show this card. It also waits for
 * the fetch to settle so it never flashes during a tab switch.
 */
export const shouldShowMyListsEmptyState = ({
	ownership,
	isSearchTab,
	hasSearchQuery,
	contentFilter,
	archivedView,
	hasSourceFilter,
	isLoading,
	isFetching,
	itemCount,
}: MyListsEmptyStateParams): boolean =>
	ownership === ListOwnership.MINE &&
	!isSearchTab &&
	!hasSearchQuery &&
	contentFilter === ListContentFilter.ALL &&
	archivedView === ArchivedView.HIDE &&
	!hasSourceFilter &&
	!isLoading &&
	!isFetching &&
	// Coerce: itemCount is a string at runtime (raw SQL COUNT), so `=== 0` alone would never match.
	Number(itemCount) === 0;
